// WorkerMap — where the people on the clock were WHEN THEY CLOCKED IN.
//
// The owner, 2026-09-10: "Could we have a map in Right Now tab that shows
// workers location, by default zoom in to see all workers."
//
// ── The one sentence this component must never get wrong ───────────────────
//
// **These pins are not live.** `time_entries.latitude/longitude` is written
// once, by the crew phone, at the moment the shift starts
// (apps/field/geo.js → routes/field.js). Nothing updates it afterwards: there
// is no second fix, no clock-out fix on an open entry, no tracking of any
// kind. A pin from an eight-hour shift is eight hours old.
//
// So every label here says "clocked in", never "is". A dispatcher who sends
// somebody across town on the strength of a stale pin is worse off than one
// who had no map, and that failure would be invisible — the map would look
// exactly as right as it does now. The caption is load-bearing product, not
// decoration, and WorkerMap.test.js asserts the words.
//
// ── …and the second one: a pin is APPROXIMATE ──────────────────────────────
//
// A phone answering from a cached cell-tower position is routinely a mile out
// while reporting four decimal places, which is eleven metres. `gpsAccuracy`
// (apps/shared/geoFix.js) reads a radius where one exists and draws it TO
// SCALE via `metresPerPixel`; `time_entries` has no accuracy column yet, so
// today that is null on every row and the pin renders with the word
// "approximate" beside it instead. Both paths are covered, because the column
// is being added separately and this must not wait on it or break when it
// lands.
//
// ── No new request, and no new gate ────────────────────────────────────────
//
// This component FETCHES NOTHING. It is handed `entries` — the `clocked_in`
// array Now.js already has from the one `GET /api/dispatch/now` that screen
// polls every 30s. That is deliberate on two counts:
//
//   · /api/ is rate limited to 1000 requests per 15 minutes per IP and a crew
//     shares one (server/utils/rateLimits.js). A map that polled would spend
//     that budget on a picture of data already in memory.
//   · The PERMISSION is `requireDispatch` on that one endpoint — admin, or
//     `timesheets.approve` / `manage_team` / `manage_all`
//     (server/routes/dispatch.js). Taking the data from the caller means the
//     map is gated by exactly that and by nothing of its own invention. There
//     is no role check in this file, and there must never be one: a worker
//     cannot reach a map of their colleagues because they cannot reach the
//     response it is drawn from.
//
// ── Static, not interactive, and that is the design ────────────────────────
//
// See apps/shared/tileMap.js for why there is no map library. The short of it:
// a draggable map in the middle of a screen you scroll eats the scroll gesture
// on a phone, and what was asked for is a default view that fits everybody —
// which is a computation, not an interaction. Tapping a pin opens the SAME
// detail sheet as tapping the row, so there is one way to learn about a person
// and one link out to a real map.

import React, { useEffect, useRef, useState } from 'react';
import { SectionLabel } from '../../../ui/fw';
import './WorkerMap.css';
import { gpsFix, gpsAccuracy, accuracyText } from '../../shared/geoFix';
import {
  fitView, tilesFor, pinAt, metresPerPixel,
  TILE_SIZE, OSM_ATTRIBUTION, OSM_COPYRIGHT_URL,
} from '../../shared/tileMap';

/** How long to wait for the first tile before calling the basemap unreachable. */
export const TILE_TIMEOUT_MS = 6000;

/**
 * The people who can be drawn, and the people who cannot.
 *
 * Both halves are returned because the second one is the honest part: a map
 * showing three of eight pins, with nothing saying so, reads as "five people
 * are not working". `gpsFix` is the app's one reader of these columns and
 * already handles both the `lat/lng` aliasing this endpoint uses and the
 * `latitude/longitude` every other one does.
 */
export function workerPins(entries) {
  const pins = [];
  const without = [];
  for (const e of (entries || [])) {
    if (!e) continue;
    const fix = gpsFix(e);
    if (!fix) { without.push(e); continue; }
    pins.push({
      entry: e,
      id: e.entry_id || `${e.user_id}-${e.job_id}`,
      name: e.name || 'Unknown',
      initials: e.initials || '?',
      lat: fix.lat,
      lng: fix.lng,
      accuracy: gpsAccuracy(e),
    });
  }
  return { pins, without };
}

/**
 * The sentence under the map about who is NOT on it.
 *
 * Returns '' when everybody on the clock has a fix — a line saying "0 people
 * have no location" is noise, and noise is what teaches people to stop reading
 * the line that matters.
 */
export function missingFixLine(pinned, missing) {
  if (!missing) return '';
  const total = pinned + missing;
  const who = missing === 1 ? '1 person' : `${missing} people`;
  return `${who} of ${total} on the clock sent no location, so they are not on the map.`;
}

/**
 * The box's size, measured. Same shape as RecordList's useNarrowContainer and
 * for the same reason: only the container knows how much room it has, and this
 * one appears full-bleed on a phone and inside a wider column on a desktop.
 *
 * Seeded at zero rather than at a guess. A guessed box would compute a zoom
 * for a width that does not exist and paint tiles for somewhere else for one
 * frame; zero means "not measured", the component says so, and the first
 * ResizeObserver callback replaces it. jsdom has no ResizeObserver, so a suite
 * that wants pins has to supply one — which is honest: there is no layout in
 * jsdom to measure, and a test that pretended otherwise would be asserting
 * against a number it invented.
 */
export function useBoxSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const RO = window.ResizeObserver;

    // A measurement of 0 is IGNORED, not stored. Three reasons, and the first
    // one bit immediately:
    //
    //   · the belt-and-braces `measure(el.clientWidth, …)` after `observe`
    //     runs in jsdom too, where clientWidth is always 0 — so without this
    //     guard it overwrote the observer's real answer with zero and the map
    //     rendered a frame and nothing else. Caught by WorkerMap.test.js's
    //     "draws one pin per person", which is the whole point of it existing.
    //   · a container inside a closed Sheet or a collapsed panel reports 0,
    //     and blanking the map on the strength of that would make it depend on
    //     whether somebody had opened a panel yet (RecordList's note, same).
    //   · zero is not a box, so there is nothing it could correctly mean.
    const measure = (width, height) => {
      const w = Math.round(width || 0);
      const h = Math.round(height || 0);
      if (w <= 0 || h <= 0) return;
      setSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    };

    if (typeof RO === 'function') {
      const ro = new RO((list) => {
        for (const entry of list || []) {
          const box = entry && entry.contentRect;
          if (box) measure(box.width, box.height);
          else measure(el.clientWidth, el.clientHeight);
        }
      });
      ro.observe(el);
      measure(el.clientWidth, el.clientHeight);
      return () => ro.disconnect();
    }

    const onResize = () => measure(el.clientWidth, el.clientHeight);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [ref]);

  return size;
}

/**
 * One pin: a tap target with the person's initials in it.
 *
 * A button, not a decorated div, because it does something — it opens the same
 * sheet the row opens. `aria-label` carries the whole sentence rather than the
 * initials, since "MR" read out on its own is not a location.
 *
 * The accuracy ring is a sibling and is only rendered when there IS a radius
 * to draw and that radius is big enough to see. A halo of two pixels says
 * "this is exact" as loudly as no halo at all, and a halo drawn at a fixed
 * size when the metres are unknown would be a decoration people would read as
 * a measurement.
 */
function Pin({ pin, view, onOpen }) {
  const at = pinAt(view, pin);
  if (!at) return null;
  const label = pin.accuracy
    ? `${pin.name}, clocked in near this point, accurate to about ${Math.round(pin.accuracy)} metres`
    : `${pin.name}, approximate clock-in location`;

  const mpp = metresPerPixel(pin.lat, view.zoom);
  const ringPx = pin.accuracy && mpp > 0 ? (pin.accuracy / mpp) * 2 : 0;

  return (
    <>
      {ringPx >= 24 ? (
        <span
          className="dp-map__ring"
          aria-hidden="true"
          style={{ left: `${at.left}px`, top: `${at.top}px`, width: `${ringPx}px`, height: `${ringPx}px` }}
        />
      ) : null}
      <button
        type="button"
        className="dp-map__pin"
        style={{ left: `${at.left}px`, top: `${at.top}px` }}
        data-pin={pin.id}
        aria-label={label}
        title={label}
        onClick={() => onOpen && onOpen(pin.entry)}
      >
        <span className="dp-map__pin-initials" aria-hidden="true">{pin.initials}</span>
      </button>
    </>
  );
}

export default function WorkerMap({ entries, onOpen }) {
  const boxRef = useRef(null);
  const { width, height } = useBoxSize(boxRef);
  const { pins, without } = workerPins(entries);

  // Tiles: how many answered, how many refused, and whether we gave up
  // waiting. Keyed on the view so a re-fit starts the question again rather
  // than carrying a verdict about a different set of tiles.
  const [tiles, setTiles] = useState({ ok: 0, bad: 0, timedOut: false });
  const view = pins.length ? fitView(pins, { width, height }) : null;
  const viewKey = view ? `${view.zoom}/${Math.round(view.originX)}/${Math.round(view.originY)}` : '';

  useEffect(() => {
    setTiles({ ok: 0, bad: 0, timedOut: false });
    if (!viewKey) return undefined;
    const t = setTimeout(() => {
      setTiles((prev) => (prev.ok > 0 ? prev : { ...prev, timedOut: true }));
    }, TILE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [viewKey]);

  const boxes = view ? tilesFor(view) : [];
  // "The basemap is not coming." Either every tile refused, or nothing arrived
  // before the timeout. Both are the same fact to a dispatcher and both need
  // the same sentence — an offline phone in a yard is the ordinary case here,
  // not an exception.
  const noTiles = !!view && tiles.ok === 0 && (tiles.timedOut || (boxes.length > 0 && tiles.bad >= boxes.length));

  const noteAbout = missingFixLine(pins.length, without.length);

  return (
    <section className="dp-map" data-map={pins.length ? 'pins' : 'empty'}>
      <SectionLabel>Where they clocked in</SectionLabel>

      {pins.length === 0 ? (
        // No frame, no grey box, no zero-pin map. A sentence, which is the
        // whole answer: there is nothing to draw and here is why.
        <p className="dp-map__quiet" data-map-empty="true">
          {without.length
            ? `No clock-in locations to map. ${missingFixLine(0, without.length)}`
            : 'No clock-in locations to map. Nobody is on the clock.'}
        </p>
      ) : (
        <>
          <div className="dp-map__canvas" ref={boxRef} data-tiles={noTiles ? 'down' : 'up'}>
            {noTiles ? (
              // Deliberately NOT an .fw-notice and deliberately not phrased as
              // a load failure: the screen loaded, the data is correct, and one
              // decorative sub-resource is unreachable. It says what to do
              // instead, because "each row links out" is true and useful.
              <p className="dp-map__down">
                The map needs a connection. Every person below still shows their
                coordinates and a link to a full map.
              </p>
            ) : (
              <>
                {boxes.map((t) => (
                  <img
                    key={t.key}
                    className="dp-map__tile"
                    src={t.url}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                    width={TILE_SIZE}
                    height={TILE_SIZE}
                    style={{ left: `${t.left}px`, top: `${t.top}px` }}
                    onLoad={() => setTiles((p) => ({ ...p, ok: p.ok + 1 }))}
                    onError={() => setTiles((p) => ({ ...p, bad: p.bad + 1 }))}
                  />
                ))}
                {view ? pins.map((p) => (
                  <Pin key={p.id} pin={p} view={view} onOpen={onOpen} />
                )) : null}
              </>
            )}
          </div>

          {/* The load-bearing sentence. See the header: never "is", always
              "clocked in", and always the word approximate. */}
          <p className="dp-map__caption">
            Each pin is where that phone said it was when the shift started — a
            clock-in location, not live tracking. Positions are approximate.
          </p>
          {pins.some((p) => p.accuracy) ? (
            <p className="dp-map__caption">
              A ring shows how far out the reading could be
              {` (${pins.filter((p) => p.accuracy).map((p) => accuracyText(p.entry)).join(', ')})`}.
            </p>
          ) : null}
          {noteAbout ? <p className="dp-map__caption" data-map-missing="true">{noteAbout}</p> : null}
          {!noTiles ? (
            <p className="dp-map__attrib">
              <a href={OSM_COPYRIGHT_URL} target="_blank" rel="noopener noreferrer">{OSM_ATTRIBUTION}</a>
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
